// src/components/Sidebar.tsx
"use client";

import { Home, Target, Hammer, Users, Trophy, Settings, LogOut } from "lucide-react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { ProfileModal } from "./ProfileModal";
import { BlockiesAvatar } from "./BlockiesAvatar";
import { WalletAuthButton } from "./WalletAuthButton";
import { useState } from "react";
import { useConnection } from "@/contexts/connection-context";
import { useGetCampaignUser } from "@/hooks/challenges";
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from "@/config/campaign";

export function Sidebar() {
  const pathname = usePathname();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const { isAuthenticated, walletAddress, disconnectWallet } = useConnection();
  const { data: campaignUser } = useGetCampaignUser(CAMPAIGN_ID, CAMPAIGN_GROUP_ID, isAuthenticated);

  // Get user display info - only use campaign user name or truncated address
  const getUserDisplayInfo = () => {
    if (campaignUser?.name) {
      return {
        name: campaignUser.name,
        address: walletAddress
      };
    }
    if (walletAddress) {
      return {
        name: `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`,
        address: walletAddress
      };
    }
    return null;
  };

  const userInfo = getUserDisplayInfo();

  const handleSignOut = () => {
    disconnectWallet();
  };

  const menuItems = [
    { icon: Home, label: "Home", path: "/", isExternal: false },
    {
      icon: Target,
      label: "Quests",
      path: "/quests",
      badge: "NEW",
      isExternal: false,
    },
    {
      icon: Hammer,
      label: "Rhuna Forge",
      path: "/daily-lottery",
      isExternal: false,
    },
    { icon: Users, label: "Referrals", path: "/referrals", isExternal: false },
    {
      icon: Trophy,
      label: "Leaderboard",
      path: "/leaderboard",
      isExternal: false,
    },
  ];


  const isActive = (path: string) => pathname === path;

  return (
    <>
      {/* Sidebar */}
      <aside
        className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 lg:z-50"
        style={{ backgroundColor: "#0C1113" }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4">
            <img src="https://pulsarmoney.b-cdn.net/rhuna/logo.png" alt="RHUNA" className="h-8 w-auto" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4">
            <ul className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <li key={item.path}>
                    {item.isExternal ? (
                      <a
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                          hover:bg-gray-800 text-gray-300
                        `}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                        {item.badge && (
                          <span className="ml-auto px-2 py-0.5 text-xs rounded bg-[#60FF8A] text-black">
                            {item.badge}
                          </span>
                        )}
                      </a>
                    ) : (
                      <Link
                        href={item.path}
                        className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                          ${
                            active
                              ? "bg-[#60FF8A] text-black"
                              : "hover:bg-gray-800 text-gray-300"
                          }
                        `}
                      >
                        <Icon className={`w-5 h-5 ${active ? "text-black" : ""}`} />
                        <span className="font-medium">{item.label}</span>
                        {item.badge && (
                          <span
                            className={`
                            ml-auto px-2 py-0.5 text-xs rounded
                            ${
                              active
                                ? "bg-[#60FF8A] text-black"
                                : "bg-[#60FF8A] text-black"
                            }
                          `}
                          >
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User Profile (when connected) - At the top */}
          {isAuthenticated && (
            <div className="px-4 py-4 border-b border-gray-800/50">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden">
                  <BlockiesAvatar
                    seed={walletAddress || 'user'}
                    size={12}
                    scale={4}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {userInfo?.name || (walletAddress ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` : 'User')}
                  </p>
                  <p className="text-gray-400 text-xs">
                    #{campaignUser?.rank || '--'}
                  </p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Connect Button (when not connected) */}
          {!isAuthenticated && (
            <div className="px-4 py-4">
              <WalletAuthButton />
            </div>
          )}


          {/* Settings Button - At the bottom */}
          <div className="px-4 py-4">
            <button
              onClick={() => setIsProfileModalOpen(true)}
              className="flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors hover:bg-gray-800 hover:text-white w-full text-left"
              style={{ color: "#99A1AF" }}
            >
              <Settings className="mr-3 h-5 w-5" />
              Settings
            </button>
          </div>
        </div>

        {/* Profile Modal */}
        <ProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
        />
      </aside>
    </>
  );
}
